import { atom } from "jotai";
import { atomFamily } from "jotai-family";
import { atomWithMutation, atomWithQuery, queryClientAtom } from "jotai-tanstack-query";
import {
  deleteAccessoryPhotoDoc,
  moveAccessoryPhotoDoc,
  moveSinglePhoto,
  removeAccessoryPhoto,
  replacePhoto,
  setPhotoCaption,
  uploadAccessoryPhoto,
} from "@/modules/plus/photos/plus.photos.api";
import { accessoryPhotosQueryOptions, photosKeys } from "@/modules/plus/photos/plus.photos.query";
import type { AccessoryPhoto } from "@/modules/plus/plus.schema";
import type { AtomContainer } from "@/state";

// One photo as the UI sees it after merging baseline + uploads. Flat shape —
// the underlying AccessoryPhoto doc isn't exposed so the UI never has to
// reach into Firestore-doc structure.
export interface ResolvedPhoto {
  url: string;
  source: "baseline" | "uploaded";
  // Present only on user uploads. Stable per upload (Storage path includes a
  // timestamp); used as the removal identifier and React `key`.
  imagePath?: string;
  uploadedBy?: string;
}

export class PhotosAtoms {
  constructor(private readonly atoms: AtomContainer) {}

  public uploadedQuery = atomWithQuery(() => accessoryPhotosQueryOptions());

  // Per-code resolved gallery. atomFamily memoizes by code so each PhotoInline
  // subscribes only to its own slice — a single accessory upload doesn't
  // re-render every other row's gallery.
  public byCode = atomFamily((code: string) =>
    atom<ResolvedPhoto[]>((get) => {
      const baseline = get(this.atoms.data.accessoryBaseline);
      const uploaded = get(this.uploadedQuery).data ?? {};
      const list: ResolvedPhoto[] = [];
      const baselineUrl = baseline[code];
      if (baselineUrl) {
        list.push({ url: baselineUrl, source: "baseline" });
      }
      for (const doc of uploaded[code] ?? []) {
        list.push({
          url: doc.image_url,
          source: "uploaded",
          imagePath: doc.image_path,
          uploadedBy: doc.uploaded_by,
        });
      }
      return list;
    }),
  );

  public upload = atomWithMutation((get) => ({
    // The server stamps the uploader from the session — no uploadedBy here.
    mutationFn: async (args: { code: string; file: File }) => {
      return uploadAccessoryPhoto({ code: args.code, file: args.file });
    },
    onSuccess: () => {
      get(queryClientAtom).invalidateQueries({ queryKey: photosKeys.all });
    },
  }));

  public remove = atomWithMutation((get) => ({
    mutationFn: async (args: { code: string; imagePath: string }) => {
      await removeAccessoryPhoto(args);
    },
    onSuccess: () => {
      get(queryClientAtom).invalidateQueries({ queryKey: photosKeys.all });
    },
  }));

  // --- Orphan re-assignment (admin) ------------------------------------

  // A photo doc whose code doesn't attach to any model. The app's per-model
  // gallery matches by exact code or by `${modelCode}-` prefix; anything that
  // fails both is invisible in the UI and gets surfaced here for review.
  public orphans = atom<Array<{ code: string; photos: AccessoryPhoto[] }>>((get) => {
    const uploaded = get(this.uploadedQuery).data ?? {};
    const models = get(this.atoms.data.models);
    const modelCodes = Object.keys(models);
    const isOrphan = (code: string): boolean => {
      if (code in models) return false;
      for (const mc of modelCodes) {
        if (code.startsWith(`${mc}-`)) return false;
      }
      return true;
    };
    const out: Array<{ code: string; photos: AccessoryPhoto[] }> = [];
    for (const [code, photos] of Object.entries(uploaded)) {
      if (isOrphan(code) && photos.length > 0) {
        out.push({ code, photos });
      }
    }
    // Stable order: alphabetical by code
    out.sort((a, b) => a.code.localeCompare(b.code));
    return out;
  });

  // Resolve a candidate code to the model it would attach to under the app's
  // gallery matching rules. Returns null if the code stays orphan.
  public resolveCodeToModel = atomFamily((code: string) =>
    atom<{ modelCode: string; matchType: "exact" | "prefix" } | null>((get) => {
      const models = get(this.atoms.data.models);
      if (code in models) {
        return { modelCode: code, matchType: "exact" };
      }
      // Prefer the longest model-code prefix so 4340CB wins over 4340 when
      // both match — the more-specific attach is more useful.
      let bestMatch: { modelCode: string; matchType: "exact" | "prefix" } | null = null;
      for (const mc of Object.keys(models)) {
        if (code.startsWith(`${mc}-`) && (!bestMatch || mc.length > bestMatch.modelCode.length)) {
          bestMatch = { modelCode: mc, matchType: "prefix" };
        }
      }
      return bestMatch;
    }),
  );

  public renameOrphan = atomWithMutation((get) => ({
    mutationFn: async (args: { oldCode: string; newCode: string }) => {
      await moveAccessoryPhotoDoc(args);
    },
    onSuccess: () => {
      get(queryClientAtom).invalidateQueries({ queryKey: photosKeys.all });
    },
  }));

  public deleteOrphan = atomWithMutation((get) => ({
    mutationFn: async (code: string) => {
      await deleteAccessoryPhotoDoc(code);
    },
    onSuccess: () => {
      get(queryClientAtom).invalidateQueries({ queryKey: photosKeys.all });
    },
  }));

  // --- Gallery (browse all uploads) ------------------------------------

  // Flat view of every user-uploaded photo across every code. Baseline
  // photos are not included — the gallery only lets you manage user uploads
  // (baseline photos are read-only, shipped in the build).
  public allPhotosFlat = atom<GalleryPhoto[]>((get) => {
    const uploaded = get(this.uploadedQuery).data ?? {};
    const out: GalleryPhoto[] = [];
    for (const [code, photos] of Object.entries(uploaded)) {
      for (const p of photos) {
        out.push({
          code,
          url: p.image_url,
          imagePath: p.image_path,
          uploadedBy: p.uploaded_by,
          uploadedAt: p.uploaded_at,
          caption: p.caption,
        });
      }
    }
    // Newest first — image_path embeds a timestamp so this sort is stable
    // even when uploaded_at is missing or malformed on legacy entries.
    out.sort((a, b) => b.imagePath.localeCompare(a.imagePath));
    return out;
  });

  public galleryFilter = atom<GalleryFilter>({
    codeSubstr: "",
    uploader: "",
    modelCode: "",
  });

  public galleryUploaders = atom<string[]>((get) => {
    const photos = get(this.allPhotosFlat);
    return Array.from(new Set(photos.map((p) => p.uploadedBy))).sort((a, b) => a.localeCompare(b));
  });

  public galleryFiltered = atom<GalleryPhoto[]>((get) => {
    const all = get(this.allPhotosFlat);
    const filter = get(this.galleryFilter);
    const codeNeedle = filter.codeSubstr.trim().toLowerCase();
    const uploader = filter.uploader;
    const modelCode = filter.modelCode;

    return all.filter((p) => {
      if (codeNeedle && !p.code.toLowerCase().includes(codeNeedle)) {
        return false;
      }
      if (uploader && p.uploadedBy !== uploader) {
        return false;
      }
      if (modelCode) {
        if (p.code !== modelCode && !p.code.startsWith(`${modelCode}-`)) {
          return false;
        }
      }
      return true;
    });
  });

  public movePhoto = atomWithMutation((get) => ({
    mutationFn: async (args: { oldCode: string; newCode: string; imagePath: string }) => {
      await moveSinglePhoto(args);
    },
    onSuccess: () => {
      get(queryClientAtom).invalidateQueries({ queryKey: photosKeys.all });
    },
  }));

  public replace = atomWithMutation((get) => ({
    mutationFn: async (args: { code: string; imagePath: string; file: File }) => {
      return replacePhoto(args);
    },
    onSuccess: () => {
      get(queryClientAtom).invalidateQueries({ queryKey: photosKeys.all });
    },
  }));

  public setCaption = atomWithMutation((get) => ({
    mutationFn: async (args: { code: string; imagePath: string; caption: string }) => {
      await setPhotoCaption(args);
    },
    onSuccess: () => {
      get(queryClientAtom).invalidateQueries({ queryKey: photosKeys.all });
    },
  }));
}

// One flat gallery item — expands the { code: photos[] } shape from the API
// into per-photo rows the grid can render directly.
export interface GalleryPhoto {
  code: string;
  url: string;
  imagePath: string;
  uploadedBy: string;
  uploadedAt: string; // ISO
  caption?: string;
}

export interface GalleryFilter {
  codeSubstr: string;
  uploader: string;
  modelCode: string;
}
